import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CallToActionLink,
  FlexBox,
  IconFontAwesome,
  Panel,
  Pill,
  TextBody,
  TextDense,
  TextH2
} from '@directsoftware/ui-kit-web-admin';
import ReactI18n from 'react-i18n';
import { useSelector } from 'react-redux';
import FeatureListItem from '../shared-v2/feature-list-item';
import ListingsDetailsContentBlock from '../shared-v2/listings-details-content-block';
import { useDetectMobile } from '../../../../shared/hooks/useDetectMobile';
import {
  fetchWlsRoomAvailability,
  fetchWlsRoomPricing
} from '../../../utils/wls-api-client';
import { createRequestTracker } from '../../../utils/wls-booking-range';
import { buildWlsCheckoutUrl } from '../../../utils/wls-checkout-url';
import { selectListingsDetailsSingleState } from '../../../redux/slices/listingsDetailsSingle';
import { formatCurrency } from '../../../../shared/helpers';

const ChooseRoom = () => {
  const { isMobile } = useDetectMobile();
  const brand = useSelector(state => state.brand);
  const listing = useSelector(state => state.listing);
  const listingsDetailsState = useSelector(selectListingsDetailsSingleState);
  const [roomResults, setRoomResults] = useState({});
  const translate = ReactI18n.getIntlMessage;
  const roomTypes = listing.room_types || [];
  const bookingRange = listingsDetailsState.bookingRange;
  const guests = listingsDetailsState.guests;
  const currentRequest = useRef({ bookingRange, guests });
  currentRequest.current = { bookingRange, guests };
  const availabilityRequests = useRef(createRequestTracker()).current;
  const pricingRequests = useRef(createRequestTracker()).current;

  useEffect(() => {
    if (!bookingRange || roomTypes.length === 0) {
      availabilityRequests.start();
      pricingRequests.start();
      setRoomResults({});
      return;
    }

    const availabilityToken = availabilityRequests.start();
    const pricingToken = pricingRequests.start();
    const isLatestRequest = () =>
      availabilityRequests.isLatest(availabilityToken) &&
      pricingRequests.isLatest(pricingToken) &&
      currentRequest.current.bookingRange === bookingRange &&
      currentRequest.current.guests === guests;

    setRoomResults(
      roomTypes.reduce((results, roomType) => {
        results[roomType.id] = {};
        return results;
      }, {})
    );

    roomTypes.forEach(roomType => {
      fetchWlsRoomAvailability(brand, roomType.listing_id, {
        unit_id: roomType.default_unit_id,
        booking_range: JSON.stringify(bookingRange),
        guests
      })
        .then(response => {
          if (!isLatestRequest()) return;
          setRoomResults(results => ({
            ...results,
            [roomType.id]: {
              ...results[roomType.id],
              availability: response.data
            }
          }));
        })
        .catch(error => {
          if (!isLatestRequest()) return;
          console.error(error);
          setRoomResults(results => ({
            ...results,
            [roomType.id]: {
              ...results[roomType.id],
              availabilityError: true
            }
          }));
        });

      fetchWlsRoomPricing(brand, roomType.listing_id, {
        booking_range: JSON.stringify(bookingRange),
        num_guests: guests,
        addon_fee_ids: listingsDetailsState.addonFeeIds,
        coupon_code: listingsDetailsState.couponCode
      })
        .then(response => {
          if (!isLatestRequest()) return;
          setRoomResults(results => ({
            ...results,
            [roomType.id]: {
              ...results[roomType.id],
              pricing: response.data
            }
          }));
        })
        .catch(error => {
          if (!isLatestRequest()) return;
          console.error(error);
          setRoomResults(results => ({
            ...results,
            [roomType.id]: {
              ...results[roomType.id],
              pricingError: true
            }
          }));
        });
    });
  }, [
    availabilityRequests,
    bookingRange,
    brand,
    guests,
    listingsDetailsState.addonFeeIds,
    listingsDetailsState.couponCode,
    pricingRequests,
    roomTypes
  ]);

  const getSelectUrl = roomType => {
    const params = new URLSearchParams(window.location.search);
    params.set('room', roomType.id);
    const query = params.toString();
    return `/listings/${roomType.slug}/unit/${roomType.default_unit_id}${
      query ? `?${query}` : ''
    }`;
  };

  const getCheckoutUrl = roomType =>
    buildWlsCheckoutUrl({
      listingSlug: listing.slug,
      listingId: roomType.listing_id,
      checkInDate: listingsDetailsState.checkInDate,
      checkOutDate: listingsDetailsState.checkOutDate,
      guests,
      addonFeeIds: listingsDetailsState.addonFeeIds,
      couponCode: listingsDetailsState.couponCode,
      customLocation: listingsDetailsState.customLocation,
      deliveryLocation: listingsDetailsState.deliveryLocation,
      quoteId: listingsDetailsState.quoteId
    });

  const renderPrice = (roomType, result) => {
    const currency = roomType.currency || listing.currency;
    if (bookingRange) {
      if (result?.availabilityError || result?.pricingError) {
        return <TextDense>Pricing unavailable, try again</TextDense>;
      }
      const pricing = result?.pricing;
      if (!pricing) return null;

      const averagePrice =
        pricing.promotional_subtotal < pricing.subtotal
          ? pricing.average_nightly_promotional_price
          : pricing.average_nightly_price;

      return (
        <>
          <TextDense>
            {translate(`global.parsers.currency.${currency}`, {
              value: Math.round(averagePrice)
            })}{' '}
            {translate('cx.global.pricing.avg_per_night')}
          </TextDense>
          <TextBody weight="semibold">
            {translate('cx.global.total')}:{' '}
            {formatCurrency(pricing.total.toFixed(2), currency)}
          </TextBody>
        </>
      );
    }

    if (roomType.average_default_nightly_price == null) return null;
    return (
      <TextDense>
        From{' '}
        {translate(`global.parsers.currency.${currency}`, {
          value: Math.round(roomType.average_default_nightly_price)
        })}{' '}
        {translate('cx.global.pricing.avg_per_night')}
      </TextDense>
    );
  };

  const orderedRoomTypes = [...roomTypes].sort((a, b) => {
    const aSelected = a.id === listing.selected_room_type_id;
    const bSelected = b.id === listing.selected_room_type_id;
    return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
  });

  return (
    <ListingsDetailsContentBlock label="Choose your room" id="details-rooms">
      <FlexBox flexDirection="column" gap="m">
        {orderedRoomTypes.map(roomType => {
          const result = roomResults[roomType.id] || {};
          const availability = result.availability;
          const pricing = result.pricing;
          const isSelected = roomType.id === listing.selected_room_type_id;
          const isUnavailable = bookingRange && availability?.bookable === false;
          const unitsAvailable = availability?.units_available;
          const showUnitsAvailable =
            availability?.bookable &&
            Number.isInteger(unitsAvailable) &&
            unitsAvailable >= 1 &&
            unitsAvailable <= 4;
          const imageUrl = roomType.image?.large || roomType.image?.original;
          const canBook =
            bookingRange &&
            !isSelected &&
            availability?.bookable &&
            pricing;

          return (
            <Panel
              key={roomType.id}
              padding="s"
              borderWidth={isSelected ? 'xs' : 'xxs'}
              presetBorderColors={isSelected ? 'blue' : undefined}
            >
              <FlexBox
                alignItems={isMobile ? 'stretch' : 'flex-start'}
                flexDirection={isMobile ? 'column' : 'row'}
                gap="m"
                style={{ width: '100%', minWidth: 0 }}
              >
                <Box
                  flex={isMobile ? '1' : '0 0 240px'}
                  style={{
                    width: isMobile ? '100%' : 240,
                    height: isMobile ? 180 : 160,
                    maxHeight: 180,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    className="photoItem"
                    setPositionRelative
                    style={{ height: '100%', overflow: 'hidden' }}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <Box className="photoItem__icon">
                        <IconFontAwesome name="image" />
                      </Box>
                    )}
                  </Box>
                </Box>
                <FlexBox
                  flex="1"
                  flexDirection="column"
                  gap="s"
                  alignItems="stretch"
                  style={{ minWidth: 0 }}
                >
                  <FlexBox
                    alignItems="center"
                    flexWrap="wrap"
                    gap="s"
                    style={{ width: '100%', minWidth: 0 }}
                  >
                    <Box
                      flex="1 1 auto"
                      style={{ minWidth: 0, overflowWrap: 'anywhere' }}
                    >
                      <TextH2
                        textColor={isUnavailable ? 'disabled' : 'dark-gray'}
                      >
                        {roomType.name}
                      </TextH2>
                    </Box>
                    {isSelected && (
                      <Pill color="blue" style={{ flex: '0 0 auto' }}>
                        Selected
                      </Pill>
                    )}
                  </FlexBox>
                  <FlexBox
                    flexDirection={isMobile ? 'column' : 'row'}
                    flexWrap="wrap"
                    alignItems="flex-start"
                    gap="m"
                    renderAs="ul"
                  >
                    <FeatureListItem
                      iconName="users"
                      label={`${translate('cx.global.amenities.sleeps')} ${
                        roomType.guest_count
                      }`}
                    />
                    <FeatureListItem
                      iconName="bed"
                      label={`${roomType.bedroom_count} ${translate(
                        'cx.global.amenities.beds'
                      )}`}
                    />
                    <FeatureListItem
                      iconName="shower"
                      label={`${roomType.bathroom_count} ${translate(
                        'cx.global.amenities.baths'
                      )}`}
                    />
                  </FlexBox>
                  {isUnavailable && (
                    <TextDense textColor="disabled">
                      Not available for your dates
                    </TextDense>
                  )}
                  <FlexBox
                    flexDirection="column"
                    alignItems="flex-end"
                    gap="xs"
                    style={{
                      minWidth: 0,
                      textAlign: 'right'
                    }}
                  >
                    {renderPrice(roomType, result)}
                    {showUnitsAvailable && (
                      <TextDense textColor="red">
                        {unitsAvailable === 1
                          ? 'Only 1 left'
                          : `${unitsAvailable} left`}
                      </TextDense>
                    )}
                    {!isSelected && !bookingRange && (
                      <CallToActionLink
                        href={getSelectUrl(roomType)}
                        onClick={event => {
                          event.preventDefault();
                          window.location.assign(getSelectUrl(roomType));
                        }}
                      >
                        Select
                      </CallToActionLink>
                    )}
                    {canBook && (
                      <CallToActionLink href={getCheckoutUrl(roomType)}>
                        Book Now
                      </CallToActionLink>
                    )}
                  </FlexBox>
                </FlexBox>
              </FlexBox>
            </Panel>
          );
        })}
      </FlexBox>
    </ListingsDetailsContentBlock>
  );
};

export default ChooseRoom;
